import { useEffect, useRef } from "react";
import QRCode from "react-qr-code";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatDateBR } from "@/utils/dateFormat";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { getPaymentStatusApiV1MyAccountPaymentStatusGatewayPaymentIdGet } from "@/backend-sdk/client/sdk.gen";

interface PixPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  copyPaste?: string | null;
  dueDate?: string | null;
  gatewayPaymentId?: string | null;
  /** Texto extra exibido após a descrição padrão. */
  description?: React.ReactNode;
  /** Botão secundário opcional (ex.: "Ir para o painel"). */
  footerAction?: { label: string; onClick: () => void } | null;
  /** Chamado quando o status de pagamento confirma (paid=true). */
  onPaid: () => void;
}

export const PixPaymentDialog = ({
  open,
  onOpenChange,
  copyPaste,
  dueDate,
  gatewayPaymentId,
  description,
  footerAction,
  onPaid,
}: PixPaymentDialogProps) => {
  // Sempre a versão mais recente do callback, sem reiniciar o polling.
  const onPaidRef = useRef(onPaid);
  useEffect(() => {
    onPaidRef.current = onPaid;
  });
  // One-shot guard: evita notificar mais de uma vez para o mesmo pagamento.
  const notifiedPaymentRef = useRef<string | null>(null);

  // Reabrir o diálogo (ex.: nova tentativa idempotente do mesmo pagamento)
  // deve permitir notificar novamente caso o status tenha confirmado.
  useEffect(() => {
    if (open) notifiedPaymentRef.current = null;
  }, [open]);

  // Polling do status do PIX: quando confirmado, notifica o parent.
  useEffect(() => {
    if (!open || !gatewayPaymentId) return;

    let active = true;
    const check = async () => {
      try {
        const { data } =
          await getPaymentStatusApiV1MyAccountPaymentStatusGatewayPaymentIdGet({
            path: { gateway_payment_id: gatewayPaymentId },
            throwOnError: true,
          });
        if (
          active &&
          data?.paid &&
          notifiedPaymentRef.current !== gatewayPaymentId
        ) {
          notifiedPaymentRef.current = gatewayPaymentId;
          onPaidRef.current();
        }
      } catch {
        // silencioso; tenta de novo no próximo ciclo
      }
    };

    check();
    const interval = setInterval(check, 3000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [open, gatewayPaymentId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Pague com PIX</DialogTitle>
          <DialogDescription>
            Escaneie o QR Code ou copie o código para concluir o pagamento.
            {description ? <> {description}</> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4">
          {copyPaste && (
            <>
              <div
                role="img"
                aria-label="QR Code PIX"
                className="bg-white p-3 rounded-lg border border-slate-200"
              >
                <QRCode value={copyPaste} size={224} />
              </div>
              <div className="w-full space-y-2">
                <Label>PIX copia e cola</Label>
                <div className="flex gap-2">
                  <Input readOnly value={copyPaste} className="text-xs" />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(copyPaste);
                      toast({ title: "Código PIX copiado!" });
                    }}
                  >
                    Copiar código
                  </Button>
                </div>
              </div>
            </>
          )}
          {dueDate && (
            <p className="text-xs text-slate-500">
              Vencimento: {formatDateBR(dueDate)}
            </p>
          )}
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Aguardando confirmação do pagamento...
          </div>
          {footerAction && (
            <Button
              variant="outline"
              className="w-full"
              onClick={footerAction.onClick}
            >
              {footerAction.label}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
